import { useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { IPhone } from './iphone';
import { AppHomeScreen } from './app-home-screen';

// How far down the screen the band's top has to rise before the phone comes in.
const ARRIVE_LINE = 0.35;

/**
 * Stages the phone as danchiano's /new professionals phone is, writing to the node directly so nothing re-renders:
 * - `data-staged`, on mount: the script takes the hidden state over from the CSS-only first paint (-styles/app-phone.css),
 *   whose 4 s fallback would otherwise show the phone if the script never ran.
 * - `data-arrived`, once the band's top rises past a line a third of the way down the screen — or at once if the whole
 *   band fits on screen: the phone spins in and its screen's demo starts from the closed card.
 * - `data-offscreen`, while none of the phone is on screen: the screen's loop stops outright.
 */
function usePhoneStage<T extends HTMLElement>() {
	const ref = useRef<T>(null);
	useEffect(() => {
		const node = ref.current;
		const band = node?.closest('section');
		if (!node || !band) return;
		node.setAttribute('data-staged', '');

		const arrival: IntersectionObserver[] = [];
		const arrive = () => {
			node.setAttribute('data-arrived', '');
			for (const observer of arrival) observer.disconnect();
		};
		// The root is the strip from the top of the screen down to the arrival line, so the band enters it when its
		// top crosses the line.
		arrival.push(
			new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.isIntersecting)) arrive();
				},
				{ rootMargin: `0px 0px -${Math.round((1 - ARRIVE_LINE) * 100)}% 0px` },
			),
		);
		// Or there is room for all of it: on a screen tall enough to show the whole band, it comes in without a scroll.
		arrival.push(
			new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.intersectionRatio >= 0.99)) arrive();
				},
				{ threshold: [0.99] },
			),
		);
		for (const observer of arrival) observer.observe(band);

		const offscreen = new IntersectionObserver((entries) => {
			for (const entry of entries) node.toggleAttribute('data-offscreen', !entry.isIntersecting);
		});
		offscreen.observe(node);

		return () => {
			for (const observer of arrival) observer.disconnect();
			offscreen.disconnect();
		};
	}, []);
	return ref;
}

/**
 * The app-download band's phone: the 3D iPhone playing the donors app's mobile home, where a finger opens the orange
 * totals card to show what it holds and closes it again. It spins in once and then holds still.
 *
 * The screen is laid out once at 300 × 637 px and zoomed to whichever width the phone has, so the two sizes below
 * must move together with the zoom in `AppHomeScreen`.
 */
export function AppPhone() {
	const { t } = useLingui();
	const ref = usePhoneStage<HTMLDivElement>();
	return (
		<div ref={ref} className="app-phone relative">
			{/* The floor shadow stays flat while the phone turns above it. */}
			<span aria-hidden className="absolute inset-x-4 -bottom-1 h-8 rounded-[50%] bg-black/25 blur-xl" />
			<IPhone label={t`Worldcoo app preview`} className="[--phone-w:12.5rem] md:[--phone-w:19rem]">
				<AppHomeScreen />
			</IPhone>
		</div>
	);
}
